import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Info } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal, Chapter } from "@/components/Shared";

const PricingPage = () => {
  const { t } = useLanguage();
  usePageTitle(t.nav.pricing);
  const p = t.pricing;

  return (
    <main data-testid="pricing-page" className="pt-[76px]">
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-white py-20 lg:py-28">
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_10%,transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="text-center">
            <div className="flex justify-center">
              <Chapter index="01" label={p.eyebrow} />
            </div>
            <h1 className="mx-auto max-w-3xl font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl text-balance">
              {p.title}
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base text-slate-600 sm:text-lg">{p.subtitle}</p>
          </Reveal>

          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {p.plans.map((plan, i) => {
              const featured = i === 1;
              return (
                <Reveal key={plan.name} delay={i * 0.12} className="h-full">
                  <div
                    data-testid={`pricing-card-${i}`}
                    className={`relative flex h-full flex-col rounded-2xl p-8 transition-transform duration-500 ${
                      featured
                        ? "border-2 border-emerald-400 bg-[#0A0F1D] text-white shadow-[0_32px_64px_-20px_rgba(16,185,129,0.35)] lg:-translate-y-3"
                        : "border border-slate-200 bg-white hover:-translate-y-1.5"
                    }`}
                  >
                    {featured && (
                      <span
                        data-testid="pricing-recommended-badge"
                        className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-emerald-400 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-widest text-emerald-950"
                      >
                        {p.recommended}
                      </span>
                    )}
                    <h2 className={`font-display text-lg font-bold ${featured ? "text-white" : "text-slate-900"}`}>
                      {plan.name}
                    </h2>
                    <p className="mt-4 flex items-baseline gap-1">
                      <span
                        className={`font-mono-orbit text-4xl font-extrabold ${featured ? "text-white" : "text-slate-900"}`}
                      >
                        {plan.price}
                      </span>
                      <span className={`text-sm font-medium ${featured ? "text-slate-400" : "text-slate-500"}`}>
                        {p.perMonth}
                      </span>
                    </p>

                    <div className={`mt-6 border-t pt-6 ${featured ? "border-white/10" : "border-slate-100"}`}>
                      <p
                        className={`text-[11px] font-bold uppercase tracking-[0.18em] ${
                          featured ? "text-emerald-400" : "text-slate-400"
                        }`}
                      >
                        {p.modules}
                      </p>
                      <ul className="mt-3 space-y-2.5">
                        {plan.modules.map((mod) => (
                          <li
                            key={mod}
                            className={`flex items-center gap-2.5 text-sm font-medium ${
                              featured ? "text-slate-200" : "text-slate-700"
                            }`}
                          >
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                            {mod}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={`mt-6 border-t pt-6 ${featured ? "border-white/10" : "border-slate-100"}`}>
                      <p
                        className={`text-[11px] font-bold uppercase tracking-[0.18em] ${
                          featured ? "text-emerald-400" : "text-slate-400"
                        }`}
                      >
                        {p.perStore}
                      </p>
                      <div className="mt-3 space-y-2">
                        {plan.storePrices.map((price, j) => (
                          <div key={p.ranges[j]} className="flex items-center justify-between text-sm">
                            <span className={featured ? "text-slate-400" : "text-slate-500"}>{p.ranges[j]}</span>
                            <span
                              className={`font-mono-orbit font-bold ${featured ? "text-white" : "text-slate-900"}`}
                            >
                              {price}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between text-sm">
                          <span className={featured ? "text-slate-400" : "text-slate-500"}>{p.ranges[3]}</span>
                          <span className={`font-bold ${featured ? "text-emerald-400" : "text-blue-600"}`}>
                            {p.onQuote}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className={`mt-6 text-xs ${featured ? "text-slate-400" : "text-slate-500"}`}>
                      {p.deployment} : {p.from} <span className="font-mono-orbit font-bold">{plan.deploy}</span>
                    </p>

                    <Link
                      to="/demo"
                      data-testid={`pricing-cta-${i}`}
                      className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold transition-colors duration-300 ${
                        featured
                          ? "bg-emerald-400 text-emerald-950 hover:bg-emerald-300"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      {p.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2}>
            <p
              data-testid="pricing-note"
              className="mx-auto mt-12 flex max-w-2xl items-start justify-center gap-2 text-center text-sm text-slate-500"
            >
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p.note}
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default PricingPage;
